import { Link } from 'react-router-dom'
import { LandingPage } from '../../components'

const TermsOfUse = () => {
  return (
    <LandingPage title='Terms of Use'>
      <div className='space-y-6'>
        <p className='text-sm text-muted-foreground'>
          Last updated: June 17, 2026
        </p>

        <section className='space-y-2 mt-8'>
          <h2>Introduction</h2>
          <p>
            These Terms of Use (the &apos;Terms&apos;) form a legal agreement
            between you and wowaudit governing your access to and use of
            wowaudit.com and its subdomains (the &apos;Service&apos;). wowaudit
            is a World of Warcraft guild management tool.
          </p>
          <p>
            By accessing or using the Service, you agree to be bound by these
            Terms and by our{' '}
            <Link to='/privacy-policy' className='underline hover:opacity-80'>
              Privacy Policy
            </Link>
            . If you do not agree to these Terms, you may not use the Service.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>License to use the Service</h2>
          <p>
            Subject to your compliance with these Terms, we grant you a limited,
            non-exclusive, non-transferable, non-sublicensable, and revocable
            license to access and use the Service for your personal and guild
            management purposes. All rights not expressly granted to you are
            reserved by wowaudit and its licensors.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Acceptable use</h2>
          <p>
            To protect the Service, its data, and its users, you agree that you
            will not:
          </p>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              Access data not intended for you, or log into an account or system
              you are not authorized to access.
            </li>
            <li>
              Use any bot, scraper, crawler, or other automated means to access,
              harvest, or collect data from the Service, except for generally
              available search engine crawlers acting in good faith.
            </li>
            <li>
              Probe, scan, or test the vulnerability of any wowaudit system or
              network, or breach or circumvent any security or authentication
              measures.
            </li>
            <li>
              Interfere with or disrupt the Service, or impose an unreasonable
              or disproportionately large load on our infrastructure.
            </li>
            <li>
              Copy, modify, reverse engineer, decompile, disassemble, or create
              derivative works from the Service or any part of it that is not
              your own.
            </li>
            <li>
              Use any data or content from the Service to develop or train
              generative artificial intelligence, machine learning models, or
              related technologies.
            </li>
            <li>
              Resell, sublicense, redistribute, or otherwise commercially
              exploit data obtained from the Service.
            </li>
            <li>
              Circumvent or violate any technical measures, access controls, or
              premium entitlement checks.
            </li>
            <li>
              Distribute viruses, malware, or any other harmful code, or violate
              any applicable laws, rules, or regulations.
            </li>
          </ul>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>User content</h2>
          <p>
            The Service lets you submit content such as guild applications,
            free-text answers, file attachments, and feedback (&apos;User
            Content&apos;). You retain ownership of your User Content. You grant
            wowaudit a worldwide, non-exclusive, royalty-free license to host,
            store, process, and display your User Content as needed to operate
            and provide the Service.
          </p>
          <p>
            You are responsible for ensuring your User Content is lawful and
            does not infringe the rights of others. We may remove User Content
            at any time if we believe it violates these Terms or is otherwise
            objectionable.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Premium subscriptions and payments</h2>
          <p>
            Some features require an active premium membership, which is
            provided through Patreon. Billing, renewal, and cancellation of your
            membership are handled by Patreon and governed by Patreon&apos;s
            terms. Your access to premium features is tied to your active
            Patreon membership status and may end when that membership lapses.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Intellectual property</h2>
          <p>
            The Service, including the wowaudit name, logo, software, and site
            content, is owned by wowaudit and protected by intellectual property
            laws. These Terms do not grant you any rights to wowaudit&apos;s
            trademarks or service marks.
          </p>
          <p>
            World of Warcraft, its data, and related trademarks are the property
            of Blizzard Entertainment. wowaudit is not affiliated with or
            endorsed by Blizzard Entertainment.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Third-party services</h2>
          <p>
            The Service integrates with third-party services, including Blizzard
            / Battle.net, Discord, Patreon, Warcraft Logs, and Raider.IO. Your
            use of those services is governed by their own terms and policies.
            wowaudit is not responsible for the content, availability, or
            practices of any third-party service.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Disclaimer of warranties</h2>
          <p>
            The Service is provided &apos;as is&apos; and &apos;as
            available&apos; without warranties of any kind, whether express,
            implied, or statutory. We do not warrant that the Service will be
            accurate, reliable, uninterrupted, error-free, or secure, or that
            audit data fetched from third parties will be complete or available
            at any particular time.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Limitation of liability</h2>
          <p>
            To the maximum extent permitted by applicable law, wowaudit will not
            be liable for any indirect, incidental, special, consequential, or
            punitive damages, or for any loss of data, goodwill, or other
            intangible losses, resulting from your use of or inability to use
            the Service.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Termination</h2>
          <p>
            We may suspend or terminate your access to the Service at any time
            if you breach these Terms or if we are required to do so for legal
            or security reasons.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Contact</h2>
          <p>
            If you have any questions about these Terms, please reach out at{' '}
            <a
              href='mailto:info@wowaudit.com'
              className='underline hover:opacity-80'
            >
              info@wowaudit.com
            </a>
            .
          </p>
        </section>
      </div>
    </LandingPage>
  )
}

export default TermsOfUse
